import { Translation } from "../hooks/useTranslations";

const fr: Translation = {
  drivers: {
    edit: "Modifier le conducteur",
    new: "Nouveau conducteur",
    singular: "Conducteur",
    plural: "Conducteurs",
    delete: "Supprimer le conducteur",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce conducteur ?",
    },
  },

  subsidiaries: {
    delete: "Supprimer la filiale",
    edit: "Modifier la filiale",
    new: "Nouvelle filiale",
    singular: "Filiale",
    plural: "Filiales",
    versions: "Versions de la filiale",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette filiale ?",
    },

    errors: {
      notMatchingSubsidiaryFound: "Aucune filiale correspondante trouvée.",
      nameAlreadyExists: "Nom de la filiale déjà utilisé",
      countryISOCodeAlreadyExists: "Le code ISO du pays spécifié est déjà défini pour une autre filiale.",
      inUseByCars: "La filiale est encore utilisée par des voitures et ne peut pas être supprimée.",
      inUseByCarOrders: "La filiale est encore utilisée par un achat de voiture et ne peut pas être supprimée.",
      missingVatServiceCode: "Code TVA services de la filiale manquant",
      missingVatDeductionCode: "Code de déduction TVA de la filiale manquant",
      missingCountryIsoCode: "Code ISO du pays de la filiale manquant",
      missingSemiramisOrganizationCode: "Code d'organisation Semiramis de la filiale manquant",
    },
  },

  manufacturers: {
    delete: "Supprimer le fabricant",
    edit: "Modifier le fabricant",
    new: "Nouveau fabricant",
    singular: "Fabricant",
    plural: "Fabricants",
    versions: "Versions du fabricant",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce fabricant ?",
    },

    errors: {
      nameAlreadyUsed: "Il existe déjà un fabricant avec le même nom.",
      inUseByCars: "Le fabricant est encore utilisé par des voitures et ne peut pas être supprimé.",
      inUseByCarOrders: "Le fabricant est encore utilisé par un achat de voiture et ne peut pas être supprimé.",
    },
  },

  cars: {
    all: "Toutes les voitures",
    delete: "Supprimer la voiture",
    edit: "Modifier la voiture",
    new: "Nouvelle voiture",
    singular: "Voiture",
    plural: "Voitures",
    versions: "Versions de la voiture",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette voiture ?",
    },

    errors: {
      vinAlreadyUsed: "Il existe déjà une voiture avec le numéro de châssis spécifié.",
      carReadNotAllowed: "Lecture de la voiture non autorisée",
    },
  },

  suppliers: {
    delete: "Supprimer le fournisseur",
    edit: "Modifier le fournisseur",
    new: "Nouveau fournisseur",
    singular: "Fournisseur",
    plural: "Fournisseurs",
    versions: "Versions du fournisseur",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce fournisseur ?",
    },

    errors: {
      nameAlreadyUsed: "Il existe déjà un fournisseur avec le même nom.",
      inUseByCarOrders: "Le fournisseur est encore utilisé par des achats de voitures et ne peut pas être supprimé.",
    },
  },

  carOrders: {
    all: "Tous les achats de voitures",
    delete: "Supprimer l'achat de voiture",
    edit: "Modifier l'achat de voiture",
    new: "Nouvel achat de voiture",
    singular: "Achat de voiture",
    plural: "Achats de voitures",
    versions: "Versions de l'achat de voiture",

    questions: {
      delete: "Voulez-vous vraiment supprimer cet achat de voiture ?",
      deleteLinkedPlanning:
        'Cet achat de voiture est lié à une planification de voiture. Son statut sera remis à "À commander".',
    },
    errors: {
      carOrderDateAfterSoldDate: "La date d'achat du véhicule ne peut pas être postérieure à la date de vente.",
    },
  },

  carPlates: {
    delete: "Supprimer la plaque",
    edit: "Modifier la plaque",
    new: "Nouvelle plaque",
    singular: "Plaque",
    plural: "Plaques",
    versions: "Versions de la plaque",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette plaque ?",
    },

    errors: {
      plateOverlapPeriodSub: "La plaque est déjà utilisée par une autre voiture dans la même filiale.",
      plateOverlapPeriodCar: "La période spécifiée chevauche une autre plaque.",
      carPlateOutOfSubsidiaryPeriod: "La période de la plaque ne correspond pas à la période de la filiale.",
    },
  },

  carDrivers: {
    delete: "Supprimer le conducteur de la voiture",
    edit: "Modifier le conducteur de la voiture",
    new: "Nouveau conducteur de la voiture",
    singular: "Conducteur de la voiture",
    plural: "Conducteurs de la voiture",
    versions: "Versions du conducteur de la voiture",
    sendMileageEmail: "Envoyer l'e-mail de kilométrage au nouveau conducteur",
    mileageEmailFirstAssignment: "L'e-mail ne peut pas être envoyé : c'est le premier conducteur de cette voiture.",
    mileageEmailNotificationsDisabled: (flag: string) =>
      `L'e-mail ne peut pas être envoyé : l'option « ${flag} » est activée pour le conducteur.`,
    mileageEmailNoEmail: "L'e-mail ne peut pas être envoyé : ce conducteur n'a pas d'adresse e-mail.",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce conducteur de la voiture ?",
    },

    errors: {
      overlapsPeriod: "La période spécifiée chevauche celle d'un autre conducteur de voiture.",
      overlapsPeriodPool: "La période spécifiée chevauche une autre du parc automobile.",
      carDriverOutOfSubsidiaryPeriod: "La période du conducteur ne correspond pas à la période de la filiale.",
      delegationLanguageNotFound: "La langue sélectionnée n'est pas disponible pour les délégations.",
      delegationSubsidiaryNotConfigured:
        "Les données de la délégation ne sont pas configurées pour le code d'organisation Semiramis de la filiale.",
    },
  },

  carLeasings: {
    delete: "Supprimer le leasing de la voiture",
    edit: "Modifier le leasing de la voiture",
    conclude: "Conclure le leasing de la voiture",
    new: "Nouveau leasing de la voiture",
    singular: "Leasing de la voiture",
    plural: "Leasings de voitures",
    versions: "Versions du leasing de la voiture",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce leasing de voiture ?",
      conclude: "Voulez-vous vraiment conclure ce leasing de voiture ?",
    },

    errors: {
      alreadyHasLeasing: "Un leasing est déjà défini pour la voiture spécifiée.",
      expirationDateCantMatchContractDate: "La date d'expiration ne peut pas correspondre à la date du contrat",
      expirationDateCantBeBeforeContractDate: "La date d'expiration ne peut pas être antérieure à la date du contrat",
    },
  },

  mileages: {
    delete: "Supprimer le kilométrage",
    edit: "Modifier le kilométrage",
    new: "Nouveau kilométrage",
    singular: "Kilométrage",
    plural: "Kilométrages",
    versions: "Versions du kilométrage",
    sendReminder: "Envoyer un rappel de kilométrage",
    sendReminders: "Envoyer des rappels",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce kilométrage ?",
      sendReminder:
        "Voulez-vous envoyer au conducteur actuel un e-mail lui rappelant de saisir le kilométrage à jour de ce véhicule ?",
    },

    reminderDialog: {
      recipient: "Destinataire",
      lastReminder: "Dernier rappel de kilométrage envoyé au conducteur",
      noReminderSent: "Aucun rappel envoyé",
      automaticReminderRule:
        "Fleet Manager envoie automatiquement un rappel de kilométrage aux conducteurs des véhicules sans kilométrage au cours des 3 derniers mois, mais ignore les conducteurs qui en ont déjà reçu un au cours des 3 derniers jours. Si vous mettez à jour la date du dernier rappel, cet e-mail compte comme le dernier rappel envoyé et le conducteur ne recevra pas le rappel automatique pendant les 3 prochains jours, même si les 3 mois expirent entre-temps.",
      updateLastReminder: "Mettre à jour la date du dernier rappel",
      driverNotificationsDisabled: (flag) =>
        `L'e-mail ne peut pas être envoyé : le conducteur a l'indicateur "${flag}" activé.`,
      driverNoEmail: "L'e-mail ne peut pas être envoyé : le conducteur n'a pas d'adresse e-mail.",
    },

    remindersDialog: {
      question:
        "Voulez-vous envoyer aux conducteurs actuels des véhicules actifs un e-mail leur rappelant de saisir le kilométrage à jour de leur véhicule ?",
      recipients: "Destinataires",
      allDrivers: "Tous les conducteurs",
      onlyOverdue: "Uniquement kilométrage échu (plus de 3 mois)",
      allSubsidiaries: "Toutes les filiales",
      summary: (drivers, emails) =>
        `Conducteurs à notifier : ${drivers} (e-mails à envoyer : ${emails}, un par véhicule).`,
      excluded: (excluded) =>
        `Conducteurs exclus car ils ont désactivé les notifications ou n'ont pas d'adresse e-mail : ${excluded}.`,
      noRecipients: "Aucun conducteur à notifier.",
      onlyOverdueInfo: (option) =>
        `Avec "${option}", seuls les conducteurs des véhicules dont le dernier kilométrage date de plus de 3 mois ou qui n'ont jamais eu de kilométrage sont notifiés.`,
      updateLastReminderInfo: (switchLabel) =>
        `Fleet Manager envoie aussi un rappel automatique aux conducteurs dont le kilométrage est échu, mais ignore ceux qui en ont reçu un au cours des 3 derniers jours. En activant "${switchLabel}", la date du dernier rappel des conducteurs notifiés est fixée à aujourd'hui : pendant les 3 prochains jours, ils ne recevront pas le rappel automatique, même si les 3 mois expirent entre-temps.`,
      backgroundInfo:
        "Les rappels sont envoyés en arrière-plan : vous pouvez fermer cette fenêtre et continuer à travailler. Vous serez averti à la fin de l'envoi.",
      alreadySending:
        "Un envoi de rappels de kilométrage est déjà en cours : vous pourrez en lancer un autre une fois terminé.",
      sent: "Rappels de kilométrage envoyés.",
    },

    notNotifiedDialog: {
      title: "Rappels de kilométrage non envoyés",
      text: "Le rappel de kilométrage n'a pas pu être envoyé aux conducteurs suivants :",
    },

    errors: {
      mileageWithThisDateAlreadyExists: "Pour la date sélectionnée, le même kilométrage a déjà été enregistré.",
      distanceLowerPrevRecord: "Le kilométrage saisi est inférieur à l'enregistrement précédent.",
      distanceHigherNextRecord: "Le kilométrage est supérieur à l'enregistrement suivant.",
      mileageOutOfSubsidiaryPeriod: "La date du kilométrage ne correspond pas à la période de la filiale.",
      mileageOutOfCarDriverPeriod:
        "La date du kilométrage est en dehors de la période pendant laquelle la voiture t’est attribuée.",
      mileageMustBeBetween: (prevDate, prevDistance, nextDate, nextDistance) =>
        `Pour la date sélectionnée, la distance doit être comprise entre ${prevDistance} (le ${prevDate}) et ${nextDistance} (le ${nextDate}).`,
      mileageMustBeGreater: (distance, date) =>
        `Pour la date sélectionnée, la distance doit être supérieure à ${distance} (le ${date}).`,
      mileageMustBeLower: (distance, date) =>
        `Pour la date sélectionnée, la distance doit être inférieure à ${distance} (le ${date}).`,
      reminderMissingCurrentDriver: "L'e-mail ne peut pas être envoyé : le véhicule n'a pas de conducteur actuel.",
      reminderDriverMissingEmail: "L'e-mail ne peut pas être envoyé : le conducteur n'a pas d'adresse e-mail.",
      reminderDriverNotificationsDisabled:
        "L'e-mail ne peut pas être envoyé : le conducteur a désactivé les notifications.",
      reminderSubsidiaryNotAllowed: "Vous n'avez pas accès à la filiale sélectionnée.",
    },
  },

  defaultTasks: {
    new: "Nouvelle tâche par défaut",
    edit: "Modifier la tâche par défaut",
    delete: "Supprimer la tâche par défaut",
    singular: "Tâche par défaut",
    plural: "Tâches par défaut",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette tâche par défaut ?",
    },
  },

  customTasks: {
    set: "Définir des tâches personnalisées",
    create: "Créer des tâches personnalisées",
    edit: "Modifier les tâches personnalisées",
    singular: "Tâche personnalisée",
    plural: "Tâches personnalisées",

    questions: {
      customize: "La filiale utilise actuellement les tâches par défaut. Voulez-vous les personnaliser ?",
    },

    errors: {
      taskAlreadySelected: "La tâche a déjà été sélectionnée.",
    },
  },

  tasks: {
    delete: "Supprimer la tâche",
    rename: "Renommer la tâche",
    edit: "Modifier la tâche",
    new: "Nouvelle tâche",
    singular: "Tâche",
    plural: "Tâches",
    versions: "Versions de la tâche",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette tâche ?",
    },

    errors: {
      taskNameAlreadyExists: "Il existe déjà une tâche par défaut avec le même nom.",
      inUseByCustomTasks:
        "La tâche par défaut est encore utilisée par une tâche personnalisée et ne peut pas être supprimée.",
    },
  },

  tasksCategories: {
    delete: "Supprimer la catégorie de tâches",
    plural: "Catégories de tâches",

    questions: {
      deleteSubsidiaryCustomTasks:
        "Voulez-vous vraiment supprimer cette catégorie personnalisée et associer les tâches par défaut ?",
    },
  },

  carTasks: {
    delete: "Supprimer la tâche de la voiture",
    edit: "Modifier la tâche de la voiture",
    new: "Nouvelle tâche de la voiture",
    singular: "Tâche de la voiture",
    plural: "Tâches de la voiture",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette tâche ?",
      deleteWithWorkflow:
        "Cette tâche est associée à un workflow. Si vous continuez, la tâche ainsi que le workflow associé seront supprimés. Voulez-vous continuer ?",
    },

    errors: {
      inUseByWorkflows: "Le task de voiture est toujours utilisée par un workflow et ne peut pas être supprimée",
    },
  },

  leasingCompanies: {
    delete: "Supprimer la société de leasing",
    edit: "Modifier la société de leasing",
    new: "Nouvelle société de leasing",
    singular: "Société de leasing",
    plural: "Sociétés de leasing",
    versions: "Versions de la société de leasing",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette société de leasing ?",
    },

    errors: {
      nameAlreadyExists: "Il existe déjà une société de leasing avec le même nom.",
      inUseByCarLeasings:
        "La société de leasing est encore utilisée par un leasing de voiture et ne peut pas être supprimée.",
    },
  },

  plateNumbers: {
    singular: "Plaque",
  },

  highwayPasses: {
    delete: "Supprimer le télépéage",
    edit: "Modifier le télépéage",
    new: "Nouveau télépéage",
    singular: "Télépéage",
    plural: "Télépéages",
    versions: "Versions du télépéage",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce télépéage ?",
    },

    errors: {
      highwayPassOverlaps:
        "Un télépéage avec le même numéro et pour la période spécifiée est déjà utilisé par une autre voiture.",
      highwayPassOutOfSubsidiaryPeriod: "La période du télépéage ne correspond pas à la période de la filiale.",
    },
  },

  costTypeCategories: {
    delete: "Supprimer la catégorie de type de coût",
    edit: "Modifier la catégorie de type de coût",
    new: "Nouvelle catégorie de type de coût",
    singular: "Catégorie de type de coût",
    plural: "Catégories de type de coût",
    versions: "Versions de la catégorie de type de coût",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette catégorie de type de coût ?",
    },

    errors: {
      isUsedByCostType:
        "La catégorie de type de coût est encore utilisée par un type de coût et ne peut pas être supprimée.",
      nameAlreadyUsed: "Une catégorie de type de coût avec le même nom est déjà définie.",
    },
  },

  costTypes: {
    delete: "Supprimer le type de coût",
    edit: "Modifier le type de coût",
    new: "Nouveau type de coût",
    singular: "Type de coût",
    plural: "Types de coûts",
    versions: "Versions du type de coût",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce type de coût ?",
    },

    errors: {
      isUsedByCosts: "Le type de coût est encore utilisé par un coût et ne peut pas être supprimé.",
      nameAlreadyUsed: "Il existe déjà un type de coût avec ce nom.",
    },
  },

  costs: {
    delete: "Supprimer le coût",
    edit: "Modifier le coût",
    new: "Nouveau coût",
    singular: "Coût",
    plural: "Coûts",
    versions: "Versions du coût",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce coût ?",
    },

    errors: {
      costOutOfSubsidiaryPeriod: "La date du coût ne correspond pas à la période de la filiale.",
      missingCurrency: (currency: string) => `Impossible de convertir le coût en EUR : devise ${currency} introuvable.`,
    },
  },

  maintenanceTypes: {
    delete: "Supprimer le type de maintenance",
    edit: "Modifier le type de maintenance",
    new: "Nouveau type de maintenance",
    singular: "Type de maintenance",
    plural: "Types de maintenance",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce type de maintenance ?",
    },

    errors: {
      isUsedByMaintenance:
        "Le type de maintenance est encore utilisé par une maintenance et ne peut pas être supprimé.",
      nameAlreadyUsed: "Un type de maintenance avec le même nom existe déjà.",
    },
  },

  maintenance: {
    delete: "Supprimer la maintenance",
    edit: "Modifier la maintenance",
    new: "Nouvelle maintenance",
    singular: "Maintenance",
    plural: "Maintenances",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette maintenance ?",
    },
  },

  costsImport: {
    delete: "Supprimer l'importation des coûts",
    changeCarAssignment: "Modifier le véhicule associé",
    changeCarAssignmentDisclaimer: (plate: string, date: string) =>
      `Ce coût a été facturé à la plaque ${plate} le ${date}. Avant d'apporter une modification manuelle, veuillez vérifier les données actuelles dans Fleet Management pour vous assurer que cette correction est vraiment nécessaire.`,
    noDriverCoversDate: "Aucune des affectations listées ci-dessus ne couvre la date de ce coût.",
    missingAssignmentCostCenter: (name: string) =>
      `${name} couvre la date de ce coût mais n'a pas de centre de coût défini.`,
    carForcedManually: (date: string) => `Le véhicule a été assigné manuellement le ${date}.`,
    assignmentForcedManually: (date: string) => `L'affectation du conducteur a été forcée manuellement le ${date}.`,
    carForcedFromTo: (fromPlate: string, toPlate: string, name: string, date: string) =>
      `Cette affectation de véhicule a été modifiée de ${fromPlate} à ${toPlate} par ${name} le ${date}.`,
    removeForcedCar: "Supprimer le forçage",
    costWillBeAssignedTo: (name: string) => `Ce coût sera affecté à ${name}.`,
    forceThisDriver: "Forcer ce conducteur",
    removeAssignment: "Supprimer l'affectation",
    forcedAssignmentDoesNotCoverDate: "Cette affectation ne couvre pas la date de ce coût.",
    excludedFromExport: (plate: string) =>
      `Pour ${plate}, cette ligne n'est pas exportée vers les coûts : seuls les véhicules pkw, pkw-superior et lkw sont exportés.`,

    questions: {
      deleteImport: "Voulez-vous vraiment supprimer cette importation de coûts ?",
      importInCosts: "Voulez-vous vraiment importer ces lignes dans les coûts ?",
      forceDriver: (name: string) =>
        `Voulez-vous vraiment forcer l'affectation de ce coût à ${name} ? Cela remplace l'affectation automatique basée sur la date.`,
    },

    dkv: {
      noCarFound: (plate: string, date: string) =>
        `Pour la plaque ${plate}, aucun véhicule ne couvre le coût du ${date}`,
      noDriverFound: (plate: string, date: string) =>
        `Pour le véhicule ${plate}, aucune affectation ne couvre le coût du ${date}`,
      noSubsidiaryVatCodeFound: (productCode: string) => `Pour ${productCode} aucun code TVA trouvé`,
      noCostTypeFound: (productCode: string, category?: string) =>
        `Pour ${productCode}${category ? `/${category}` : ""} aucun type de coût trouvé`,
      noCostCenterFound: (name: string, date: string) =>
        `Pour ${name}, aucun centre de coûts ne couvre le coût du ${date}`,
      noUserCostCenterFound: (plate: string, date: string) =>
        `Pour le véhicule ${plate}, aucun centre de coûts utilisateur ne couvre le coût du ${date}`,

      errors: {
        columnTargaVeicolo: "Colonne 'Targa veicolo' introuvable",
        columnDataFattura: "Colonne 'Data della fattura' introuvable",
        columnOraTransazione: "Colonne 'Ora transazione' introuvable",
        columnValoreBaseNetto: "Colonne 'Valore base netto' introuvable",
        columnValoreBaseLordo: "Colonne 'Valore base lordo' introuvable",
        columnCodiceMerce: "Colonne 'Codice merce' introuvable",
        columnGruppoProdotti: "Colonne 'Gruppo di prodotti' introuvable",
        columnTipoMerce: "Colonne 'Tipo di merce' introuvable",
        columnPaesePedaggio: "Colonne 'Paese di pedaggio' introuvable",
        columnIva: "Colonne 'IVA' introuvable",
        columnScontoNetto: "Colonne 'Sconto netto' introuvable",
        columnScontoLordo: "Colonne 'Sconto lordo' introuvable",
        columnServiceFeeNetto: "Colonne 'Service fee netto' introuvable",
        columnQuantita: "Colonne 'Quantità' introuvable",
        columnUnita: "Colonne 'Unità' introuvable",
      },
    },

    dkvTolls: {
      errors: {
        columnDataFattura: "Colonne 'Data della fattura' introuvable",
        columnTargaVeicolo: "Colonne 'Targa veicolo' introuvable",
        columnOraUscita: "Colonne 'Ora uscita' introuvable",
        columnCodiceMerce: "Colonne 'Codice merce' introuvable",
        columnValutaServizio: "Colonne 'Valuta del servizio' introuvable",
        columnIva: "Colonne 'IVA' introuvable",
        columnValoreBaseNetto: "Colonne 'Valore base netto' introuvable",
        columnPaesePedaggio: "Colonne 'Paese di pedaggio' introuvable",
      },
    },
  },

  dueDateTypes: {
    delete: "Supprimer le type d'échéance",
    edit: "Modifier le type d'échéance",
    new: "Nouveau type d'échéance",
    singular: "Type d'échéance",
    plural: "Types d'échéances",
    versions: "Versions du type d'échéance",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce type d'échéance ?",
    },

    errors: {
      isUsedByDueDates: "Le type d'échéance est encore utilisé par une échéance et ne peut pas être supprimé.",
      nameAlreadyUsed: "Il existe déjà un type d'échéance avec le même nom.",
    },
  },

  dueDates: {
    all: "Toutes les échéances",
    delete: "Supprimer l'échéance",
    edit: "Modifier l'échéance",
    new: "Nouvelle échéance",
    singular: "Échéance",
    plural: "Échéances",
    versions: "Versions de l'échéance",
    renewPlural: "Renouveler les échéances",
    renewSingular: "Renouveler expiration",
    renewSelected: "Renouveler les échéances sélectionnées",
    setSettledSelected: "Marquer les échéances sélectionnées comme réglées",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette échéance ?",
      setSettled: "Voulez-vous vraiment marquer les échéances sélectionnées comme réglées ?",
    },

    errors: {
      dueDateOutOfSubsidiaryPeriod: "La date de l'échéance ne correspond pas à la période de la filiale.",
    },
  },

  documents: {
    add: "Ajouter un document",
    all: "Tous les documents",
    alreadyPresent: "Document déjà présent.",
    plural: "Documents",
    delete: "Supprimer le document",
    search: "Rechercher des documents",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce document ?",
      overwrite: "Voulez-vous écraser le document déjà présent ?",
    },

    errors: {
      notFound: "Le document n’a pas été trouvé",
    },
  },

  users: {
    all: "Tous les utilisateurs",
    delete: "Supprimer l'utilisateur",
    edit: "Modifier l'utilisateur",
    new: "Nouvel utilisateur",
    singular: "Utilisateur",
    plural: "Utilisateurs",

    questions: {
      delete: "Voulez-vous vraiment supprimer cet utilisateur ?",
    },

    errors: {
      noMatchingUserFound: "Aucun utilisateur correspondant trouvé.",
      rothoblaasUserWithEmailNotFound: "Aucun utilisateur Rothoblaas avec l'adresse e-mail spécifiée n'a été trouvé.",
      usersWithEmailAlreadyExists: "Il existe déjà un utilisateur avec l'email spécifié.",
      inUseByCarTasks: "L'utilisateur est encore utilisé par une tâche de voiture et ne peut pas être supprimé.",
      inUseByCarOrders: "L'utilisateur est encore utilisé par un achat de voiture et ne peut pas être supprimé.",
      inUseByCarDrivers: "L'utilisateur est encore utilisé comme conducteur et ne peut pas être supprimé.",
      usersWithEmailNotFound: "Aucun utilisateur avec l'adresse e-mail spécifiée n'a été trouvé.",
      invalidEmailDomain: "Le domaine de l'adresse e-mail n'est pas valide pour les utilisateurs Rothoblaas.",
      userSubsidiaryAlreadyExists: "L'utilisateur est déjà affecté à la filiale spécifiée.",
      userInUseAsManager: "L'utilisateur est encore utilisé comme manager et ne peut pas être supprimé.",
    },
  },

  claims: {
    all: "Tous les sinistres",
    delete: "Supprimer le sinistre",
    edit: "Modifier le sinistre",
    new: "Nouveau sinistre",
    singular: "Sinistre",
    plural: "Sinistres",
    incidentDate: "Date du sinistre",
    openingDate: "Date d'ouverture",
    closingDate: "Date de clôture",
    damageValue: "Montant du dommage",
    settledAmount: "Montant réglé",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce sinistre ?",
      switchDriver: (selectedDriver: string, currentDriver: string) =>
        `Le conducteur sélectionné est ${selectedDriver}, mais celui actuellement attribué est ${currentDriver}.<br /> Voulez-vous attribuer cette réclamation à ${currentDriver} ?`,
      switchSubsidiary: (selectedSubsidiary: string, currentSubsidiary: string) =>
        `La filiale sélectionnée est ${selectedSubsidiary}, mais celle actuellement attribuée est ${currentSubsidiary}.<br /> Voulez-vous attribuer ce sinistre à ${currentSubsidiary} ?`,
    },
  },

  fines: {
    delete: "Supprimer l'amende",
    edit: "Modifier l'amende",
    new: "Nouvelle amende",
    singular: "Amende",
    plural: "Amendes",
    verbalNumber: "Numéro de verbalisation",
    violationDate: "Date de l'infraction",
    communicationDate: "Date de communication",
    firstNotificationDate: "Date de première notification",
    amount: "Montant",
    points: "Points",
    accountant: "Comptable",
    hrReferent: "Référent RH",
    notifyDriver: "Notifier le conducteur",
    archive: "Archiver",
    notifications: "Notifications",
    sentBy: "Envoyé par",
    sentAt: "Envoyé le",
    notifyDialog: {
      recipient: "Destinataire",
      attachments: "Pièces jointes",
      noAttachments: "Sélectionnez au moins un document à envoyer.",
      addDocuments: "Ajouter des documents",
      dropDocumentsHint: "Vous pouvez aussi faire glisser les fichiers sur le tableau",
      attachmentsHint: "Les documents ajoutés ici sont aussi enregistrés dans les documents de l'amende.",
      alreadyNotified: (name: string, date: string) => `Déjà notifiée par ${name} le ${date}.`,
      driverNotificationsDisabled: (flag: string) =>
        `L'e-mail ne peut pas être envoyé : le conducteur a l'indicateur "${flag}" activé.`,
      driverNoEmail: "L'e-mail ne peut pas être envoyé : le conducteur n'a pas d'adresse e-mail.",
      userNotificationsDisabled: (flag: string) =>
        `Cet utilisateur a l'indicateur "${flag}" activé et ne peut pas recevoir la copie.`,
      userNoEmail: "Cet utilisateur n'a pas d'adresse e-mail et ne peut pas recevoir la copie.",
    },
    errors: {
      notifyArchived: "Une amende archivée ne peut pas être notifiée.",
      alreadyArchived: "L'amende est déjà archivée.",
      driverMissingEmail: "L'e-mail ne peut pas être envoyé : le conducteur n'a pas d'adresse e-mail.",
      driverNotificationsDisabled: "L'e-mail ne peut pas être envoyé : le conducteur a désactivé les notifications.",
    },
    statuses: {
      new: "Nouvelle",
      notified: "Notifiée",
      archived: "Archivée",
    },
    questions: {
      delete: "Voulez-vous vraiment supprimer cette amende ?",
      archive: "Voulez-vous vraiment archiver cette amende ?",
    },
  },

  claimTypes: {
    all: "Tous les types de sinistre",
    delete: "Supprimer le type de sinistre",
    edit: "Modifier le type de sinistre",
    new: "Nouveau type de sinistre",
    singular: "Type de sinistre",
    plural: "Types de sinistre",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce type de sinistre ?",
    },

    errors: {
      nameAlreadyUsed: "Un type de sinistre avec ce nom existe déjà.",
      inUseByClaims: "Le type de sinistre est encore utilisé dans des dossiers et ne peut pas être supprimé.",
    },
  },

  clients: {
    all: "Tous les clients",
    plural: "Clients",
    createNew: "Créer un nouveau client",
    delete: "Supprimer le client",
    search: "Rechercher des clients",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce client ?",
    },

    errors: {
      notFound: "Client introuvable.",
      missingName: "Nom du client manquant.",
      insertNotAllowed: "Vous n'êtes pas autorisé à insérer un nouveau client.",
      readNotAllowed: "Vous n'êtes pas autorisé à lire ce client.",
      updateNotAllowed: "Vous n'êtes pas autorisé à mettre à jour ce client.",
      deleteNotAllowed: "Vous n'êtes pas autorisé à supprimer ce client.",
    },
  },

  subsidiaryVatCodes: {
    delete: "Supprimer le code TVA de la filiale",
    edit: "Modifier le code TVA de la filiale",
    new: "Nouveau code TVA de la filiale",
    singular: "Code TVA de la filiale",
    plural: "Codes TVA de la filiale",
    versions: "Versions du code TVA de la filiale",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce code TVA de la filiale ?",
    },
  },

  roles: {
    delete: "Supprimer le rôle",
    edit: "Modifier le rôle",
    new: "Nouveau rôle",
    singular: "Rôle",
    plural: "Rôles",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce rôle ?",
    },
  },

  permissions: {
    plural: "Autorisations",
  },

  secrets: {
    plural: "Secrets",

    errors: {
      missingName: "Nom du secret manquant.",
      missingValue: "Valeur du secret manquante.",
    },
  },

  carPoolCostCenters: {
    delete: "Supprimer le centre de coûts du parc automobile",
    edit: "Modifier le centre de coûts du parc automobile",
    new: "Nouveau centre de coûts du parc automobile",
    singular: "Centre de coûts du parc automobile",
    plural: "Centres de coûts du parc automobile",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce centre de coûts du parc automobile ?",
    },

    errors: {
      carPoolOverlapPeriod: "Le parc automobile a déjà un centre de coûts sur cette période",
    },
  },

  otherCarAssignments: {
    delete: "Supprimer une autre affectation de voiture",
    edit: "Modifier une autre affectation de voiture",
    new: "Nouvelle autre affectation de voiture",
    singular: "Autre affectation de voiture",
    plural: "Autres affectations de voiture",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette autre affectation de voiture ?",
    },

    errors: {
      carPoolOverlapPeriod: "La voiture est déjà dans le parc automobile pendant cette période",
      carPoolOverlapPeriodWithDrivers: "La voiture a déjà un conducteur pendant cette période",
      assignmentOutOfSubsidiaryPeriod: "La période de l'affectation ne correspond pas à la période de la filiale.",
    },
  },

  carPlannings: {
    delete: "Supprimer la planification du véhicule",
    edit: "Modifier la planification du véhicule",
    new: "Nouvelle planification du véhicule",
    singular: "Planification du véhicule",
    plural: "Planifications du véhicule",
    versions: "Versions de la planification du véhicule",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette planification du véhicule ?",
    },

    errors: {
      missingSemiramisCode:
        "Filiale sans code d'organisation Semiramis. Veuillez définir le code Semiramis dans la filiale sélectionnée",
    },
  },

  carSubsidiaries: {
    delete: "Supprimer la filiale de la voiture",
    edit: "Modifier la filiale de la voiture",
    new: "Nouvelle filiale de la voiture",
    singular: "Filiale de la voiture",
    plural: "Filiales de la voiture",
    versions: "Versions de la filiale de la voiture",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette filiale de la voiture ?",
    },

    errors: {
      overlapPeriodCarSubsidiary: "La période spécifiée chevauche une autre filiale.",
      overlapPeriodCarPlate: "Certaines plaques de voiture chevauchent la période d'autres filiales.",
      overlapPeriodCarDriver: "Certains conducteurs de voiture chevauchent la période d'autres filiales.",
    },
  },

  rentalCars: {
    delete: "Supprimer la voiture de location",
    edit: "Modifier la voiture de location",
    new: "Nouvelle voiture de location",
    singular: "Voiture de location",
    plural: "Voitures de location",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette voiture de location ?",
    },

    errors: {
      overlapPeriodCar: "La période spécifiée chevauche une autre voiture de location.",
    },
  },

  userSubsidiaries: {
    delete: "Supprimer la filiale utilisateur",
    edit: "Modifier la filiale utilisateur",
    new: "Nouvelle filiale utilisateur",
    singular: "Filiale utilisateur",
    plural: "Filiales utilisateur",
    versions: "Versions de la filiale utilisateur",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette filiale utilisateur ?",
    },
  },

  userRoles: {
    delete: "Supprimer le rôle utilisateur",
    edit: "Modifier le rôle utilisateur",
    new: "Nouveau rôle utilisateur",
    singular: "Rôle utilisateur",
    plural: "Rôles utilisateur",
    versions: "Versions du rôle utilisateur",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce rôle utilisateur ?",
    },

    errors: {
      alreadyExists: "Le rôle utilisateur existe déjà",
    },
  },

  common: {
    acceptImages: "Accepter les images",
    acceptImagesDialog: "Les images téléchargées seront acceptées, et le chauffeur sera notifié par e-mail.",
    accessNotAllowed: "Accès non autorisé",
    accessNotAllowedError: "Vous n'avez pas les autorisations pour afficher cette page.",
    active: "Actif",
    action: "Action",
    accounting: "Comptabilité",
    add: "Ajouter",
    addCustomTask: "Ajouter un task personnalisé",
    addDefaultTask: "Ajouter un task par défaut",
    addCustomCarTask: "Ajouter un task voiture personnalisé",
    addDefaultCarTask: "Ajouter un task voiture par défaut",
    addDueDate: "Ajouter une échéance",
    addNew: "Ajouter nouveau",
    addNewAssignment: "Ajouter une nouvelle affectation",
    addMileage: "Ajouter un kilométrage",
    address: "Adresse",
    addTask: "Ajouter une tâche",
    addToCarPool: "Ajouter au parc automobile",
    addToHistory: "Ajouter à l'historique",
    admin: "Admin",
    administration: "Administration",
    agreement: "Accord",
    all: "Tous",
    allowCarsToHaveMultiplePlates: "Autoriser les voitures à avoir plusieurs plaques",
    allowMultiplePlates: "Autoriser plusieurs plaques",
    alreadyInCarPool: "Voiture déjà dans le parc automobile",
    alreadyPresent: "Déjà présent",
    alreadyUpdated:
      "Cet enregistrement a été mis à jour par un autre utilisateur. Actualisez la page pour obtenir la version la plus récente.",
    areaSalesManager: "Responsable des ventes régionales",
    arrived: "Arrivée",
    arrivingSoon: "Arrivera bientôt",
    ascending: "Ascendant",
    assign: "Attribuer",
    assignCar: "Attribuer une voiture",
    assignHp: "Attribuer un nouveau télépéage",
    assignNewDriver: "Attribuer un nouveau conducteur",
    assignNewPlate: "Attribuer une nouvelle plaque",
    assigned: "Attribué",
    assignedTo: "Attribué à",
    assignSubsidiary: "Attribuer une filiale",
    attachments: "Pièces jointes",
    attachmentsHelpDialog:
      "Il est possible de joindre des images et/ou des documents ; les fichiers téléchargés seront visibles par vous et par les administrateurs du système Fleet Manager.",
    avgYears: "Ø Années",
    awaitingForApproval: "En attente d'approbation",
    back: "Retour",
    birthdate: "Date de naissance",
    business: "Professionnel",
    by: "De",
    cancel: "Annuler",
    cancelled: "Annulé",
    cancelWorkflow: "Annuler le workflow",
    cancelWorkflowRequest:
      "Le flux de travail sera arrêté. Tous les téléchargements d'images en attente seront annulés. Voulez-vous continuer ?",
    car: "Voiture",
    carAssignment: "Attribution du véhicule",
    carCategory: "Catégorie de voiture",
    carInCarPool: "Voiture dans le parc automobile",
    carNotInUse: "Voiture non utilisée",
    carPool: "Parc automobile",
    carPoolCostCenters: "Centres de coûts du parc automobile",
    carOrdersWithNearTermText: {
      prefix:
        "Il y a certains achats de voitures avec une date d'arrivée proche qui ne sont pas encore attribués. Cliquez ",
      linkTitle: "ici",
      suffix: " pour les lister et les gérer.",
    },
    carReplacedRental: "Voiture de remplacement de location",
    cartax: "Taxe automobile",
    carVin: "Voiture (Châssis)",
    categories: "Catégories",
    category: "Catégorie",
    changeFlag: "Changer le drapeau",
    checkWorkflow: "Vérifier le workflow",
    clear: "Effacer",
    close: "Fermer",
    closed: "Fermé",
    awaitingDocuments: "En attente de documents",
    closingDate: "Date de clôture",
    columnNotFilterable: "Cette colonne ne peut pas être filtrée",
    filterEquals: "Égal à",
    filterRange: "De - À",
    filterMonth: "Mois",
    columnNotSortable: "Cette colonne ne peut pas être triée",
    continue: "Continuer",
    contractDate: "Date du contrat",
    contractDetails: "Détails du contrat",
    cost: "Coût",
    costInputFormNet: "Coût (Net - Devise)",
    countDate: "Date de comptage",
    country: "Pays",
    countryIsoCode: "Code ISO du pays",
    countryIsoCodeRequiredForAgreement:
      "La création d'un accord nécessite que le code ISO du pays de la filiale soit défini.",
    costCenter: "Centre de coûts",
    costCenterHistory: "Historique du centre de coûts",
    costObject: "Objet de coût",
    costImport: "Importation des coûts",
    costImports: "Importations des coûts",
    costGross: "Coût brut",
    costNet: "Coût net",
    co2: "CO2",
    create: "Créer",
    createdAt: "Créé le",
    createdBy: "Créé par",
    createDelegation: "Créer une délégation",
    createDelegationWithDriverSelection: "Créer une délégation avec sélection du conducteur",
    createDelegationForExternal: "Créer une délégation pour des personnes externes",
    createNew: "Créer nouveau",
    currency: "Devise",
    currentHighwayPass: "Télépéage actuel",
    currentDriver: "Conducteur actuel",
    currentlyInCarPool: "Cette voiture est actuellement dans le parc automobile.",
    currentlyNotUsed: "Cette voiture n'est actuellement pas utilisée.",
    currentlyAssignToDriver: "Cette voiture est actuellement attribuée à un autre conducteur.",
    currentPlateNumber: "Plaque actuelle",
    currentSubsidiary: "Filiale actuelle",
    currentVersion: "Version actuelle",
    dashboard: "Tableau de bord",
    dataConflicts: (value: string) => `Data conflicts on date ${value}`,
    dataHasBeenSavedSuccessfully: "Les données ont été enregistrées avec succès.",
    dataHasBeenUpdatedSuccesfully: "Les données ont été mises à jour avec succès.",
    date: "Date",
    datePicker: {
      clearButton: "Effacer la date",
      nextMonth: "Mois suivant",
      placeholder: "Choisir une date",
      previousMonth: "Mois précédent",
      selectMonth: "Choisir le mois",
      selectYear: "Choisir l'année",
    },
    decrease: (value: number) => `Diminuer ${value}`,
    defaultCurrency: "Devise par défaut",
    defaultLanguage: "Langue par défaut",
    delegation: "Délégation",
    delete: "Supprimer",
    deleteFilter: (filter: string) => `Supprimer les filtres pour ${filter}`,
    deleteFilters: "Supprimer les filtres",
    department: "Département",
    departments: "Départements",
    descending: "Descendant",
    description: "Description",
    diesel: "Diesel",
    discountGross: "Remise brute",
    discountNet: "Remise nette",
    distance: "Distance",
    distances: "Distances",
    distanceInMiles: "Distance en miles",
    divisionSalesManager: "Responsable des ventes de division",
    dkvProductCodes: "Codes produit DKV",
    documents: "Documents",
    done: "Terminé",
    download: "Télécharger",
    downloadFile: "Télécharger le fichier",
    dragAndDropOrUploadAnImage: "Glissez-déposez ou cliquez pour télécharger une image",
    driverAlreadyNotified:
      "Un e-mail de notification a déjà été envoyé à cet utilisateur. Souhaitez-vous le renvoyer ?",
    driverNotified: (on: string) => `Chauffeur notifié le ${on}. En attente du téléchargement des images.`,
    dragAndDropOrUploadAnFile: "Glissez-déposez ou cliquez pour télécharger un fichier",
    driverName: "Nom du pilote",
    driverSince: "Conducteur depuis",
    drivingLicenseExpirationDate: "Date d'expiration du permis de conduire",
    drivingLicenseText:
      "En saisissant dans le système la date d'expiration de votre permis de conduire, vous recevrez automatiquement une notification de rappel environ 20 jours avant son expiration. Les informations saisies ne sont ni suivies ni gérées par Fleet Management, mais sont traitées exclusivement par le système.",
    dropIt: "Déposer",
    dueDateHelpDialog: "En saisissant une date, l'expiration sera mise à jour avec la date sélectionnée.",
    dueDateVisibleToUser:
      "La sélection d'un utilisateur rend la date d'échéance visible à la fois pour l'utilisateur lui-même et pour les responsables concernés.",
    edit: "Modifier",
    editedBy: "Modifié par",
    editNotPermitted: "La modification n'est plus autorisée — plus d'un jour s'est écoulé depuis la création.",
    electric: "Électrique",
    email: "Email",
    emailSendToDriver: "Une notification par e-mail sera envoyée au chauffeur.",
    empty: "Vide",
    endDate: "Date de fin",
    engineCapacity: "Cylindrée",
    english: "Anglais",
    englishUk: "Anglais - Royaume-Uni",
    englishUs: "Anglais - États-Unis",
    englishBrasil: "Anglais - Brésil",
    enteredDate: "Date de saisie",
    enterAPhoneNumber: "Saisissez un numéro de téléphone",
    enterValidNames: "Saisissez des noms valides.",
    error: "Erreur",
    errorLoadingData: "Erreur lors du chargement des données",
    expirationDate: "Date d'expiration",
    expired: "Expiré",
    expiringSoon: "Expire bientôt",
    exportExcel: "Exporter Excel",
    fieldCannotBeEmpty: "Le champ ne peut pas être vide.",
    file: "Fichier",
    fileCsv: "Fichier CSV",
    fileNotSupported: `Le fichier n'est pas pris en charge.`,
    fileTypeSupported: (extensions: string[]) => `Types de fichiers pris en charge : ${extensions.join(", ")}.`,
    fileToLarge: "Fichier trop volumineux",
    fiscalPrice: "Prix fiscal",
    fieldContainsInvalidValueInsertBiggerOne: "Le champ contient une valeur non valide, insérez-en une plus grande.",
    fieldContainsInvalidValueInsertSmallerOne: "Le champ contient une valeur non valide, insérez-en une plus petite.",
    fieldInvalidValue: "Le champ contient une valeur non valide.",
    fieldValueInvalidFormat: "La valeur a un format non valide.",
    firstName: "Prénom",
    flag: "Drapeau",
    french: "Français",
    fringeBenefit: "Avantage en nature",
    from: "De",
    fuelCard: "Carte carburant",
    fuelType: "Type de carburant",
    gasoline: "Essence",
    generalData: "Données générales",
    generateAgreement: "Générer l'accord",
    genericError: "Une erreur est survenue, veuillez réessayer.",
    german: "Allemand",
    gpl: "GPL",
    hasImportErrors: "Contient des erreurs d'importation",
    hidden: "Masqué",
    hiddenText:
      "Comme les utilisateurs sont régulièrement importés depuis le système Rothoblaas, certains comptes pourraient ne pas être nécessaires dans le système de gestion. Ce paramètre permet de masquer un utilisateur dans la gestion des conducteurs.",
    disableNotifications: "Desactiver les notifications",
    disableNotificationsText:
      "Si ce parametre est active, les e-mails de notification automatiques ne seront pas envoyes. Les autres e-mails generes par des actions de l'utilisateur continueront a etre envoyes.",
    hideArrivalNotification: "Masquer la notification de date d'arrivée",
    hideToUser: "Masquer pour l'utilisateur",
    history: "Historique",
    hp: "CV",
    hybrid: "Hybride",
    imagesAccepted: "Images acceptées",
    imagesDeleted: "Images supprimées",
    imagesSupplied: "Images fournies",
    imperial: "Impérial (miles)",
    importInCosts: "Importer dans les coûts",
    import: "Importer",
    importCsv: "Importer CSV",
    importErrors: "Erreurs d'importation",
    importErrorsText:
      "Les centres de coûts sont importés automatiquement depuis bValue (Provisius).<br/>Les lignes ci-dessous indiquent les enregistrements qui n'ont pas pu être importés.<br/>Vérifiez les dates avec les RH et corrigez-les ci-dessous.",
    importDkv: "Importer DKV",
    importHighwayToll: "Importer les péages autoroutiers",
    importedInCosts: "Importé dans les coûts",
    increase: (value: number) => `Augmenter ${value}`,
    insert: "Insérer",
    insurance: "Assurance",
    invalidDate: "Date invalide",
    invalidLenght: "Longueur invalide",
    invalidNumber: "Nombre invalide",
    isInPool: "Est dans le parc automobile",
    isRothoblaasUser: "Est un utilisateur Rothoblaas",
    isRothoblaasUserTexts: [
      "Ce paramètre permet de distinguer les utilisateurs Rothoblaas des utilisateurs externes. Lorsqu'il est activé, le système vérifie que l'adresse e-mail est valide. Dans ce cas, le prénom et le nom sont récupérés depuis la base de données Rothoblaas. De plus, il n'est pas possible d'enregistrer un utilisateur avec une adresse e-mail Rothoblaas s'il n'existe aucune correspondance parmi les utilisateurs actifs.",
      "Lorsque le paramètre est désactivé, l'adresse e-mail reste optionnelle et aucune vérification n'est effectuée sur les utilisateurs Rothoblaas. Dans ce cas, le prénom et le nom deviennent obligatoires.",
    ],
    isSettled: "Est réglé",
    italian: "Italien",
    portuguese: "Portugais",
    withErrors: "Avec erreurs",
    withoutErrors: "Sans erreurs",
    itemsNotExported: "Non exportés",
    itemsForced: "Forcés",
    forcedBy: "Forcé par",
    keep: "Conserver",
    kilometers: "Kilomètres",
    language: "Langue",
    languageCode: "Code langue",
    lastDriver: "Dernier conducteur",
    lastEdit: "Dernière modification par",
    lastHighwayPass: "Dernier télépéage",
    lastMileage: "Dernier kilométrage",
    lastMileageRec: "Dernier enregistrement de kilométrage",
    lastName: "Nom",
    lastPlate: "Dernière plaque",
    lastRecord: "Dernier enregistrement",
    lastUpdate: "Dernière mise à jour",
    lastUpdatedBy: "Dernière mise à jour par",
    latvian: "Letton",
    leasing: "Leasing",
    lessThanDueDateRecord: "est antérieure à la date de l'un des enregistrements sélectionnés",
    licensePlateValidityEnd: "Fin de validité de la plaque",
    licensePlateValidityStart: "Début de validité de la plaque",
    lithuanian: "Lituanien",
    loading: "Chargement",
    logout: "Déconnexion",
    lkw: "PL",
    lkwAccountingNumber: "Numéro comptable PL",
    lkwAccountingNumberShort: "PL no compt.",
    manageCategories: "Gérer les catégories",
    manageClaimTypes: "Gérer les types de sinistre",
    manageCostTypeCategories: "Gérer les catégories de types de coûts",
    manageCostTypes: "Gérer les types de coûts",
    vatCodes: "Codes TVA",
    manageDefaultTasks: "Gérer les tâches par défaut",
    manageDueDateTypes: "Gérer les types d'échéance",
    manageMaintenanceTypes: "Gérer les types de maintenance",
    manager: "Responsable",
    managerEmail: "Email du responsable",
    managerName: "Nom du responsable",
    metric: "Métrique (km)",
    mileage: "Kilométrage",
    mileages: "Kilométrages",
    miles: "miles",
    missingContractDate: "Date du contrat manquante",
    missingCountry: "Pays manquant",
    missingExpirationDate: "Date d'expiration manquante",
    missingLeasingCompany: "Société de leasing manquante",
    missingRenewalDate:
      "Attention ! Aucune date de renouvellement n'a été saisie ; l'expiration ne sera pas mise à jour et sera simplement clôturée.",
    modifiedAt: "Modifié le",
    modifiedBy: "Modifié par",
    model: "Modèle",
    monthYearPicker: {
      clearButton: "Effacer le mois",
      nextYear: "Année suivante",
      placeholder: "Choisir un mois",
      previousYear: "Année précédente",
      selectYear: "Choisir l'année",
    },
    myArea: "Mon espace",
    myCar: "Ma voiture",
    myCars: "Mes voitures",
    myProfile: "Mon profil",
    myTeamCar: "Voiture de mon équipe",
    myTeamCars: "Voitures de mon équipe",
    name: "Nom",
    needsAttention: "Nécessite attention",
    ordersWithNearArrivalDate: "Achats de voitures avec arrivée imminente",
    nextNRows: (n: number) => `Prochaines ${n} lignes`,
    newLeasing: "Nouveau leasing",
    newPlateNumber: "Nouveau numéro de plaque",
    newOrder: "Nouvel achat",
    no: "Non",
    noDriver: "Aucun conducteur",
    noFileSelected: "Aucun fichier sélectionné",
    noHighwayPasses: "Aucun télépéage",
    noDueDate: "Aucune échéance",
    noDueDateSelected: "Aucune échéance sélectionnée",
    noItemsFound: "Aucun élément trouvé.",
    noLeasing: "Aucun leasing",
    noMilegaes: "Aucun kilométrage",
    notes: "Notes",
    noTasksToDo: "Aucune tâche à effectuer",
    notFound: "Non trouvé",
    notifications: "Notifications",
    notifyDriver: "Avertir le conducteur",
    notInUse: "Non utilisé",
    notPlannedPurchase: "Achat non planifié",
    notRequired: "Non requis",
    notSettled: "Non réglé",
    number: "Numéro",
    count: "Nombre",
    open: "Ouvert",
    ok: "OK",
    on: "le",
    openingDate: "Date d'ouverture",
    operation: "Opération",
    optional: "Optionnel",
    optionals: "Optionnels",
    order: "Commande",
    orderImperativ: "Commander",
    orderCar: "Commander une voiture",
    orderDate: "Date de commande",
    ordered: "Commandé",
    overview: "Vue d'ensemble",
    overwrite: "Écraser",
    otherAssignmentType: "Parc automobile / Non utilisé",
    others: "Autres",
    parking: "Parking",
    permissions: "Autorisations",
    phoneNumber: "Numéro de téléphone",
    phoneNumberIsInvalid: "Le numéro de téléphone n'est pas valide pour ce pays",
    picture: "Image",
    plannedArrivalDate: "Date d'arrivée prévue",
    plugInHybrid: "Hybride rechargeable",
    pkw: "VP",
    pkwAccountingNumber: "Numéro comptable VP",
    pkwAccountingNumberShort: "VP no compt.",
    pkwSuperior: "VP supérieur",
    pkwSuperiorAccountingNumber: "Numéro comptable VP supérieur",
    pkwSuperiorAccountingNumberShort: "VP sup. no compt.",
    pkwUp: "VP Up",
    pkwUpAccountingNumber: "Numéro comptable VP Up",
    pkwUpAccountingNumberShort: "VP Up no compt.",
    positionCode: "Code de poste",
    positionName: "Nom du poste",
    preferredLanguage: "Langue préférée",
    preview: "Aperçu",
    previousEndDateLowerStartDate: "Saisissez une date de fin précédente supérieure à sa date de début.",
    previousEndDateUndefined: "Définissez une date de fin pour l'enregistrement précédent.",
    previousNRows: (n: number) => `Précédentes ${n} lignes`,
    purchasePriceInputForm: "Prix d'achat (hors TVA - avec TVA - devise)",
    priceWithVat: "Prix avec TVA",
    priceWithoutVat: "Prix hors TVA",
    pricePerUnit: "Prix par unité",
    private: "Privé",
    privateCar: "Voiture privée",
    productCategory: "Catégorie de produit",
    productCode: "Code produit",
    productType: "Type de produit",
    purchase: "Achat",
    purchaseDate: "Date d'achat",
    purchaseType: "Achat / Leasing",
    quantity: "Quantité",
    read: "Lire",
    reAssignment: "Réaffectation",
    recipient: "Destinataire",
    resourceNotFound: "La ressource que vous recherchez est introuvable.",
    responsible: "Responsable",
    responsibleCode: "Code responsable",
    returned: "Restituée au leasing",
    regionSalesManager: "Responsable des ventes régionales",
    registrationDate: "Date d'immatriculation",
    rejectImages: "Refuser les images",
    rejected: "Rejeté",
    rejectImagesForm:
      "Les images téléchargées seront refusées et supprimées. Le chauffeur sera informé par e-mail que de nouvelles images sont requises. Vous pouvez fournir un message à inclure dans l'e-mail:",
    reloadData: "Recharger les données",
    remove: "Supprimer",
    rename: "Renommer",
    renameDocument: "Renommer le document",
    reopen: "Rouvrir",
    reopenWorkflow: "Rouvrir le workflow",
    renewalDate: "Date de renouvellement",
    roles: "Rôles",
    rolesAndPermissions: "Rôles et autorisations",
    salesRole: "Rôle commercial",
    save: "Enregistrer",
    saving: "Enregistrement",
    search: "Rechercher",
    select: "Sélectionner",
    selectAll: "Tout sélectionner",
    selectACountry: "Sélectionnez un pays",
    selectDate: "Sélectionner une date",
    selectFile: "Sélectionner un fichier",
    selectFlag: "Sélectionner un drapeau",
    semiramisOrganizationCode: "Code d'organisation Semiramis",
    sendDelegation: "Envoyer la délégation",
    send: "Envoyer",
    sendEmail: "Envoyer un e-mail",
    setAsDone: "Marquer comme terminé",
    inProgress: "En cours",
    setAsNotRequired: "Marquer comme non requis",
    setAsTodo: "Marquer comme à faire",
    setIsoCode: "Définir le code ISO",
    setNotInUse: "Définir comme non utilisé",
    setSubsidiaryIsoCode: "Définir le code ISO de la filiale",
    settings: "Paramètres",
    settled: "Réglé",
    setToday: "Définir aujourd'hui",
    showArrivalNotification: "Afficher la notification de date d'arrivée",
    showToUser: "Afficher pour l'utilisateur",
    sold: "Vendue",
    someCostCenterPeriodsOverlap: "Certaines périodes des centres de coûts se chevauchent.",
    sortAscending: "Trier par ordre croissant",
    sortDescending: "Trier par ordre décroissant",
    spanish: "Espagnol",
    startDate: "Date de début",
    startDateBeforeEndDatePrevRecord: "Saisissez une date de début postérieure à la fin de l'enregistrement précédent.",
    startDateEqualEndDate: "Saisissez une date de début antérieure à la date de fin.",
    startDateEqualPrevEndDate: "Date de début égale à la date de fin de l'enregistrement précédent",
    startDistance: "Distance initiale",
    state: "État",
    serviceFeeNet: "Frais de service nets",
    serviceFeeGross: "Frais de service bruts",
    since: "Depuis",
    soldDate: "Date de vente",
    subsidiaryRequiredForAgreement: "La création de l'accord nécessite l'association de la voiture à une filiale.",
    subsidiaryRequiredForDelegation:
      "La création d'une délégation nécessite l'association de la voiture à une filiale.",
    subsidiaryHasCustomTasks: "La filiale a des tâches personnalisées",
    subsidiaryUsesDefaultTasks: "La filiale utilise des tâches par défaut",
    tasks: "Tâches",
    taskCancelled: "Tâche annulée - aucune action requise",
    teamCar: "Voiture d'équipe",
    teamMembers: "Membres de l'équipe",
    technicalSale: "Technico-commercial",
    telepass: "Télépéage",
    testEnvironment: "Environnement de test",
    time: "Heure",
    to: "À",
    toApprove: "À approuver",
    toAssign: "attribuer",
    todo: "À faire",
    tolls: "Péages",
    toOrder: "À commander",
    total: "Total",
    type: "Type",
    typeOfUse: "Type d'utilisation",
    unassigned: "Non attribué",
    unit: "Unité",
    until: "Jusqu'à",
    userCostCenters: "Centres de coûts utilisateur",
    userCostCentersHasImportErrorsText: {
      prefix: "L'importation des centres de coûts utilisateur depuis bValue (Provisius) contient des erreurs. Cliquez",
      linkTitle: "ici",
      suffix: "pour les lister et les corriger.",
    },
    usersWithImportErrors: "Utilisateurs avec erreurs d'importation",
    userWithoutCostCenter: "Utilisateur sans centre de coûts",
    update: "Mettre à jour",
    uploadedAt: "Téléversé le",
    uploadedBy: "Téléversé par",
    uploadImages: "Téléverser des images",
    uploadImagesRequired: "Téléverser les images requises",
    vat: "TVA",
    vatCode: "Code TVA",
    vatDeductionCode: "Code de déduction TVA",
    vatServiceCode: "Code de service TVA",
    vehicleIdentificationNumber: "Numéro d'identification du véhicule",
    version: "Version",
    versions: "Versions",
    versionNumber: "Numéro de version",
    viewImages: "Voir les images",
    visibleToUser: "Visible pour l'utilisateur",
    visibleToUserInfo:
      "Ce champ permet de sélectionner un utilisateur avec lequel partager la date d'échéance. Par défaut, l'utilisateur actuellement affecté au véhicule est disponible à la sélection. Si la date d'échéance a été précédemment partagée avec un autre utilisateur, l'utilisateur actuellement affecté ainsi que l'utilisateur précédemment sélectionné pourront être choisis.",
    view: "Afficher",
    waitingForImageApproval: "En attente de validation des images",
    waitingUser: "En attente utilisateur",
    warningCloseEntities: (from: string, to: string) =>
      `La période définie (${from}-${to}) est en conflit avec d'autres enregistrements. En confirmant, ${to} sera appliquée comme date de fin pour les enregistrements listés ci-dessus.`,
    warningReOpenEntities: (from: string, to: string) =>
      `La période sélectionnée (${from}-${to}) est en conflit avec d'autres enregistrements. Sélectionnez les cases correspondant aux enregistrements que vous souhaitez reporter vers la nouvelle filiale. En confirmant, ${to} sera appliquée comme date de fin pour les enregistrements sélectionnés et comme date de début pour les nouveaux.`,
    when: "Quand",
    workflowCancelled: "Workflow annulé. Vous pouvez le rouvrir avec le bouton ci-dessous.",
    workflowImages: "Images du workflow",
    year: "Année",
    yes: "Oui",

    endDateCantMatchStartDate: "La date de fin ne peut pas correspondre à la date de début",
    endDateBeforeStartDate: "La date de fin ne peut pas être antérieure à la date de début",
    endDateAfterCurrentAssignmentStartDate:
      "La date de fin ne peut pas être postérieure à la date de début de l'affectation actuelle",
    startDateFallsInCurrentAssignmentPeriod: "La date de début se situe dans la période de l'affectation actuelle",
    endDateFallsInCurrentAssignmentPeriod: "La date de fin se situe dans la période de l'affectation actuelle",
    endDateAfterStartDateNextRec:
      "Saisissez une date de fin antérieure à la date de début de l'enregistrement suivant.",
    endDateEqualNextStartDate: "Date de fin égale à la date de début de l'enregistrement suivant",
  },
};
export default fr;
